/**
 * The song title the Lookbook HUD burns top-right (server/captionStyles.ts
 * buildHudAss) and the Studio preview mirrors (LyricsStyleModal HudOverlay).
 *
 * Origin (Oct 9 2026): a real export showed "DARK FUTURISTIC ELECTRONIC, CYBE"
 * in the corner. The web music generator's description mode sent the first
 * five words of the description as the song title when the user typed none,
 * the server kept it as user-typed (so the AI-written title was dropped), and
 * the HUD cut it at 32 characters mid-word. Songs made before the Home.tsx
 * fix keep that title, so the HUD treats prompts and placeholders as "no
 * title" and leaves the corner empty, and cuts real titles on a word.
 */

export const HUD_TITLE_MAX_CHARS = 32;

/**
 * Default titles the apps and API write when the user gave none, lowercased:
 * "Untitled Song" (music.generate / API v1), the composers' localized
 * untitled / untitledProject strings, createProject's "Untitled Project",
 * the library copy's "Uploaded song", API v1's "CLI Music Video".
 */
const PLACEHOLDER_TITLES = new Set([
  "untitled",
  "untitled song",
  "untitled project",
  "uploaded song",
  "new project",
  "cli music video",
  // newLanding/landingV2 composer.untitled + untitledProject, every locale.
  "naamloos",
  "naamloos project",
  "ohne titel",
  "projekt ohne titel",
  "sin título",
  "proyecto sin título",
  "sans titre",
  "projet sans titre",
  "senza titolo",
  "progetto senza titolo",
  "sem título",
  "projeto sem título",
  "bez tytułu",
  "projekt bez tytułu",
  "adsız",
  "adsız proje",
  "tanpa judul",
  "proyek tanpa judul",
  "chưa đặt tên",
  "dự án chưa đặt tên",
  "無題",
  "無題のプロジェクト",
  "未命名",
  "未命名项目",
  "제목 없음",
  "제목 없는 프로젝트",
]);

/** Genre, mood, instrument and vocal words a style prompt is built from. */
const STYLE_TERMS = new Set(
  (
    "pop rock edm electronic electro techno house trance dubstep trap drill " +
    "hip hop hiphop rap r&b rnb soul funk jazz blues country folk metal punk " +
    "indie lofi lo-fi ambient synthwave synth synthpop cyberpunk cyber phonk " +
    "kpop k-pop jpop j-pop reggae reggaeton latin afrobeat afrobeats " +
    "dancehall disco gospel orchestral cinematic classical acoustic ballad " +
    "emo grunge vaporwave retrowave dnb garage grime opera choir piano " +
    "guitar violin strings 808 beat beats instrumental vocal vocals voice " +
    "male female duet bpm tempo dark upbeat chill mellow moody melancholic " +
    "melancholy sad happy energetic epic dreamy atmospheric futuristic " +
    "nostalgic romantic aggressive uplifting emotional heavy soft slow fast " +
    "groovy catchy haunting ethereal lush gritty retro vintage experimental " +
    "minimal hypnotic intense calm peaceful vibe vibes style genre"
  ).split(" "),
);

const STOPWORDS = new Set("a an and the with of in on for to".split(" "));

/** Style-prompt phrases no title uses. */
const PROMPT_PATTERNS = [
  /\b\d{2,3}\s*bpm\b/,
  /\b(male|female)\s+(vocals?|voices?|singers?|rappers?)\b/,
  /\bin the style of\b/,
];

/**
 * Request phrasing ("a sad song about my", "make me a…"). Titles use it too
 * ("A Song for You", "Song for My Father"), so it only counts when the text
 * is not Title Case: descriptions are typed in sentence case.
 */
const REQUEST_PATTERNS = [
  /\b(song|songs|track|tune|music|beat|instrumental|ballad|anthem|jingle|lullaby)\s+(about|for|with|that|where)\b/,
  /\b(make|create|generate|write|compose|produce)\s+(me\s+|us\s+)?(a|an|some|my)\b/,
];

/** Composer labels for a link whose title never arrived ("YouTube link"). */
const LINK_LABEL_RE =
  /^(youtube|suno|tiktok|udio|soundcloud|spotify)\s+(link|track)$/;
/** A pasted URL / "suno.com/song/abc…" label. */
const URL_LIKE_RE = /^(https?:\/\/|www\.)|^[a-z0-9-]+(\.[a-z0-9-]+)+\//;
/** A lyric section tag: the first line of pasted lyrics ("[Verse 1]"). */
const SECTION_TAG_RE = /^\[[^\]]*\]$/;
/** Upload titles default to the file name. */
const AUDIO_EXT_RE =
  /\.(mp3|wav|m4a|aac|flac|ogg|oga|opus|weba|webm|mp4|mov|aiff?)$/i;

function collapse(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** Every Latin word outside STOPWORDS starts with a capital letter. */
function isTitleCase(text: string): boolean {
  const words = text.split(" ");
  let checked = 0;
  for (let i = 0; i < words.length; i++) {
    const word = words[i].replace(/^[^A-Za-z]+/, "");
    if (!word || STOPWORDS.has(word.toLowerCase())) continue;
    checked++;
    if (word[0] !== word[0].toUpperCase()) return false;
  }
  return checked > 0;
}

/** True for a default title written when the user gave none. */
export function isPlaceholderSongTitle(title: unknown): boolean {
  const text = collapse(typeof title === "string" ? title : "");
  return PLACEHOLDER_TITLES.has(text.toLowerCase());
}

/**
 * True when `title` is a placeholder or a style prompt / song description
 * rather than a song title, so the HUD should not show it.
 */
export function isPromptLikeSongTitle(title: unknown): boolean {
  const text = collapse(typeof title === "string" ? title : "");
  if (!text) return true;
  const lower = text.toLowerCase();
  if (PLACEHOLDER_TITLES.has(lower)) return true;
  if (LINK_LABEL_RE.test(lower) || URL_LIKE_RE.test(lower)) return true;
  if (SECTION_TAG_RE.test(text)) return true;
  // A list cut off mid-way ("…electronic, cyberpunk,") is never a title.
  if (/[,;:]$/.test(text)) return true;
  if (text.length > 80) return true;
  for (let i = 0; i < PROMPT_PATTERNS.length; i++) {
    if (PROMPT_PATTERNS[i].test(lower)) return true;
  }
  if (!isTitleCase(text)) {
    for (let i = 0; i < REQUEST_PATTERNS.length; i++) {
      if (REQUEST_PATTERNS[i].test(lower)) return true;
    }
  }
  const words = lower
    .split(/[^a-z0-9&'-]+/)
    .map((w) => w.replace(/^['-]+|['-]+$/g, ""))
    .filter(Boolean);
  if (words.length > 12) return true;
  let styleHits = 0;
  let contentWords = 0;
  let allStyle = true;
  for (let i = 0; i < words.length; i++) {
    if (STOPWORDS.has(words[i])) continue;
    contentWords++;
    if (STYLE_TERMS.has(words[i])) styleHits++;
    else allStyle = false;
  }
  // "Dark futuristic electronic, cyberpunk" / "Lo-fi, chill, rainy night":
  // a comma list carrying style words. "Hello, Goodbye" has none.
  const segments = text.split(/[,;|]/).filter((s) => s.trim()).length;
  if (segments >= 2 && styleHits >= 2) return true;
  // "Upbeat Pop Rock", "Dark Futuristic Electronic": nothing but style words.
  // Non-Latin titles produce no ASCII words, so they never land here.
  return contentWords >= 2 && allStyle;
}

/**
 * Fit an (already uppercased) label into `max` characters: whole words plus
 * an ellipsis, with no dangling comma or dash before it.
 */
export function truncateHudTitle(
  text: string,
  max: number = HUD_TITLE_MAX_CHARS,
): string {
  const clean = collapse(text).replace(/[\s,;:]+$/, "");
  if (clean.length <= max) return clean;
  const room = max - 1; // the ellipsis takes one
  const space = clean.lastIndexOf(" ", room);
  let head: string;
  if (space >= Math.ceil(room / 2)) {
    head = clean.slice(0, space);
  } else {
    // One long word (or a script without spaces): hard cut, but never
    // between the halves of a surrogate pair.
    const code = clean.charCodeAt(room - 1);
    head = clean.slice(0, code >= 0xd800 && code <= 0xdbff ? room - 1 : room);
  }
  head = head.replace(/[\s,;:.\-–—&/+(]+$/, "") || head;
  return `${head}…`;
}

/**
 * The top-right HUD label for a song / project title: uppercase, at most
 * HUD_TITLE_MAX_CHARS, cut on a word boundary. Empty when the title is a
 * placeholder or a prompt, so the HUD draws no title at all.
 */
export function hudTitleLabel(title: unknown): string {
  const text = collapse(typeof title === "string" ? title : "").replace(
    AUDIO_EXT_RE,
    "",
  );
  if (isPromptLikeSongTitle(text)) return "";
  return truncateHudTitle(text.toUpperCase());
}
